import type { FC, ReactElement } from "react"
import { Fragment } from "react"

import { Trans } from "react-i18next"
import { useCss } from "react-use"

import { ETypographyAlignment, ETypographyColor, ETypographySize, Typography } from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { CoachingChatCompletedMatrixIcon as CompletedIcon } from "../../assets"
import { useCoachingChatContext as useChatContext } from "../../context"
import { coachingChatComponentsGuards as guards } from "../../guards"
import { useCoachingChatConversationSelector as useConversationSelector } from "../../store"
import { coachingChatStyles as chatStyles } from "../../styles"

const { withModeMatrixFeedback: withGuard } = guards

const {
    conversation: { matrixOpener: classes }
} = chatStyles

/**
 * Participant Chapter CoachingMoment chat' 'Matrix Feedback' modal opener.
 * Spec {@link https://www.figma.com/design/J6qF3Pa6zeK3udtVWLskjd/Participant?node-id=19481-41969&m=dev}
 */
const Opener: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const {
        entityId,
        matrix: { handleModalOpen }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { isMatrixFeedbackCompleted }: ReturnType<typeof useConversationSelector> = useConversationSelector(entityId)

    const transCss: string = useCss(classes.wrapperInsertionStyles())

    return (
        <div className={classes.container()}>
            <button
                type="button"
                className={buildCssClassName(
                    classes.wrapper({ isMatrixFeedbackCompleted }),
                    !isMatrixFeedbackCompleted && transCss
                )}
                {...(isMatrixFeedbackCompleted
                    ? { tabIndex: -1 }
                    : {
                          tabIndex: 0,
                          onKeyDown: withPressHandler(handleModalOpen),
                          onClick: withPressHandler(handleModalOpen)
                      })}
            >
                {isMatrixFeedbackCompleted ? (
                    <Fragment>
                        <CompletedIcon />

                        <Typography
                            size={ETypographySize.ExtraSmall}
                            text={t("entities.coachingChat.chat.matrixFeedback.opener.filled.label")}
                            color={ETypographyColor.Blue500}
                            isPretty
                            className={classes.completedLabel()}
                            alignment={
                                breakpoints.xs || breakpoints.sm || breakpoints.md
                                    ? ETypographyAlignment.Center
                                    : ETypographyAlignment.Left
                            }
                        />
                    </Fragment>
                ) : (
                    <Trans
                        parent="p"
                        i18nKey="entities.coachingChat.chat.matrixFeedback.opener.notFilled.label"
                        className={classes.notCompletedLabel()}
                        components={{ strong: <strong /> }}
                    />
                )}
            </button>
        </div>
    )
}

const GuardedOpener: FC = withGuard(Opener)

GuardedOpener.displayName = "CoachingChatMatrixFeedbackModalOpener"

export { GuardedOpener as CoachingChatMatrixFeedbackModalOpener }
